import type { ApolloError } from '@apollo/client'
import queryCartId from '@shc/nuxt-shc-cart-layer/graphql/queries/queryCartId.graphql'
import mutationCartCreateEmpty from '@shc/nuxt-shc-cart-layer/graphql/mutations/mutationCartCreateEmpty.graphql'
import { useCartStore } from '../store/cart'
import type {
    Cart,
    Mutation,
    Query,
    CartProductRemoveMutation as TypeCartProductRemoveMutation,
    CartProductUpdateMutation as TypeCartProductUpdateMutation,
    GetCartIdQuery as TypeGetCartIdQuery,
} from '../graphql/types/graphql'
import queryCart from '../graphql/queries/queryCart.graphql'
import mutationCartRemoveCoupon from '../graphql/mutations/mutationCartRemoveCoupon.graphql'
import mutationCartProductUpdate from '../graphql/mutations/mutationCartProductUpdate.graphql'
import mutationCartProductRemove from '../graphql/mutations/mutationCartProductRemove.graphql'
import mutationCartApplyCoupon from '../graphql/mutations/mutationCartApplyCoupon.graphql'
import mutationCartProductsAdd from '~/graphql/mutations/mutationCartProductsAdd.graphql'
import type { CartItem } from '@/graphql/types/graphql'

type cartRemoveMultiCoupon = Pick<Mutation, 'cartRemoveMultiCoupon'>
type cartApplyMultiCoupon = Pick<Mutation, 'cartApplyMultiCoupon'>
type CartProductAddMutation = Pick<Mutation, 'cartProductsAdd'>
type CreateEmptyCartMutation = Pick<Mutation, 'cartCreateEmpty'>
type GetCartQuery = Pick<Query, 'cart'>
interface ErrorCart {
    message?: Maybe<string>
    __typename?: string
    code?: string
}

export default defineNuxtRouteMiddleware(async (to, from) => {
    const cookieCartId = useCookie<string>(useAppConfig().CART_ID)
    const store = useCartStore()
    const { $bus, $i18n, runWithContext } = useNuxtApp()

    getInitCartData()

    async function actionLoading<T = void>(action: () => Promise<T>) {
        try {
            $bus.$emit('cartActionLoading', true)
            return await action()
        } catch (error) {
            $bus.$emit('cartError', error as ApolloError)
        } finally {
            $bus.$emit('cartActionLoading', false)
        }
    }

    const createEmptyCartId = async (): Promise<void> => {
        const { mutate: createEmptyCartMutation } = useMutation<CreateEmptyCartMutation>(mutationCartCreateEmpty)
        store.actionLoader = true
        const { data } = (await createEmptyCartMutation()) ?? {}
        if (!data) {
            throw new Error('Cart creation failed')
        }
        cookieCartId.value = data.cartCreateEmpty
    }

    async function checkIfCookieCartIdIsCorrect(cookieCartId: string | null): Promise<boolean> {
        const { data: getCartId } = await runWithContext(() =>
            useAsyncQuery<TypeGetCartIdQuery>(queryCartId, {
                cartId: cookieCartId,
            }),
        )

        const cartId = getCartId.value?.cart?.cartId

        return (
            cartId === cookieCartId &&
            cartId !== null &&
            cartId !== undefined &&
            cookieCartId !== null &&
            cookieCartId !== undefined
        )
    }

    function setCartData(payload?: PartialDeep<Cart> | null) {
        if (!payload) {
            return
        }
        const data = payload as Cart
        cookieCartId.value = data.cartId ?? ''
        const [freeDelivery] = data?.freeDelivery ?? []

        data?.marketplaceCarts && store.setMarketplace(data.marketplaceCarts)
        data?.totalPrices && store.setTotals(data.totalPrices)
        data?.separateDiscountsRule && store.setCoupons(data.separateDiscountsRule)
        data?.salesCoupon && store.setSalesCoupon(data.salesCoupon)

        if (data?.errors) {
            store.setErrors(data.errors)
            showErrors(data.errors)
        }
        freeDelivery && store.setFreeDelivery(freeDelivery)

        $bus.$emit('cartUpdate')
    }

    async function getCartData() {
        const { data: cartData } = await runWithContext(() =>
            useAsyncQuery<GetCartQuery>({
                query: queryCart,
                variables: {
                    cartId: cookieCartId.value,
                },
                cache: false,
            }),
        )
        cartData.value?.cart && setCartData(cartData.value.cart as Cart)

        return cartData
    }

    function getParsedProduct() {
        return store.marketplace?.reduce((products, marketplace) => {
            marketplace?.items?.forEach((product) => {
                products.push(product)
            })
            return products
        }, [] as CartItem[])
    }

    function showErrors(errors: Maybe<ErrorCart>[]) {
        const isStopUpdate = false
        errors &&
            errors.forEach((error) => {
                if (
                    !(to.fullPath as unknown as string)?.includes('cart') &&
                    (error?.__typename === 'CartError' || error?.code === 'CART_ERRORS')
                ) {
                    return
                }

                error && $bus.$emit('cartError', { message: error.message ?? '' })
            })

        return {
            isStopUpdate,
        }
    }

    async function getInitCartData() {
        const cookieCartIdIsCorrect = await checkIfCookieCartIdIsCorrect(cookieCartId.value)
        if (!cookieCartIdIsCorrect) {
            await createEmptyCartId()
            await getCartData()
        }

        if (cookieCartId.value) {
            await getCartData()
        }

        store.cartReady = true
    }

    $bus.$on('cartProductsAdd', async (products) =>
        actionLoading(async () => {
            const { mutate: cartProductAddMutation } = useMutation<CartProductAddMutation>(mutationCartProductsAdd, {
                variables: {
                    cartId: cookieCartId.value,
                    products,
                },
            })
            const { data } = (await cartProductAddMutation()) ?? {}
            if (!data) {
                throw new Error('Cart product update failed')
            }

            if (data.cartProductsAdd?.userErrors.length && showErrors(data.cartProductsAdd?.userErrors).isStopUpdate) {
                return
            }
            setCartData(data?.cartProductsAdd?.cart)
            products.forEach(({ productId, quantity }) => {
                const cartItem = getParsedProduct().find((cartItem) => cartItem.product.sku === productId)
                $bus.$emit('cartProductAdded', { cartItemUid: cartItem?.uid ?? '', cartItem, quantity })
            })
        }),
    )

    $bus.$on('cartProductUpdate', async ({ cartItemUid, quantity }) =>
        actionLoading(async () => {
            const { mutate: cartProductUpdate } = useMutation<TypeCartProductUpdateMutation>(
                mutationCartProductUpdate,
                {
                    variables: {
                        cartId: cookieCartId.value,
                        cartItemUid,
                        quantity,
                    },
                },
            )
            const { data } = (await cartProductUpdate()) ?? {}
            if (!data) {
                throw new Error('Cart product update failed')
            }
            const cartItem = getParsedProduct().find((cartItem) => cartItem.uid === cartItemUid)

            setCartData(data?.cartProductUpdate?.cart)
            $bus.$emit('cartProductUpdated', { cartItemUid, cartItem, quantity })

            const { cartItems, subtotal } = getItemsForMiniCart(data?.cartProductUpdate)

            $bus.$emit('updateMagentoMiniCart', {
                cartItems,
                subtotal,
            })
        }),
    )

    $bus.$on('cartProductRemove', async ({ cartItemUid }) =>
        actionLoading(async () => {
            const { mutate: cartProductRemove } = useMutation<TypeCartProductRemoveMutation>(
                mutationCartProductRemove,
                {
                    variables: {
                        cartId: cookieCartId.value,
                        cartItemUid,
                    },
                },
            )
            const { data } = (await cartProductRemove()) ?? {}
            if (!data?.cartProductRemove?.cart) {
                throw new Error('Cart product remove failed')
            }

            setCartData(data?.cartProductRemove?.cart)

            $bus.$emit('notification', {
                message: $i18n.t('shcC.cart.notification.cartProductRemoved'),
                type: 'success',
            })

            const { cartItems, subtotal } = getItemsForMiniCart(data?.cartProductRemove)

            $bus.$emit('updateMagentoMiniCart', {
                cartItems,
                subtotal,
            })
        }),
    )

    $bus.$on('cartUpdate', () => {
        $bus.$emit('cartUpdated', {
            products: store.products,
            totals: store.totals,
        })
    })

    $bus.$on('discountCodeApply', async ({ code }) =>
        actionLoading(async () => {
            try {
                const { mutate: applyCouponMutation } = useMutation<cartApplyMultiCoupon>(mutationCartApplyCoupon, {
                    variables: {
                        cartId: cookieCartId.value,
                        couponCode: code,
                    },
                })
                const { data } = (await applyCouponMutation()) ?? {}
                if (!data) {
                    throw new Error('Cart product update failed after applying discount code')
                }
                data.cartApplyMultiCoupon?.cart && setCartData(data.cartApplyMultiCoupon.cart)
                $bus.$emit('discountCodeApplied', { code })
            } catch (error) {
                $bus.$emit('discountCodeApplied', { code, error: error as ApolloError })
            }
        }),
    )

    $bus.$on('discountCodeDelete', async ({ separateDiscount }) =>
        actionLoading(async () => {
            const { mutate: cartRemoveCoupon } = useMutation<cartRemoveMultiCoupon>(mutationCartRemoveCoupon, {
                variables: {
                    cartId: cookieCartId.value,
                    couponCode: separateDiscount?.couponCode ?? '',
                },
            })
            const { data } = (await cartRemoveCoupon()) ?? {}
            if (!data) {
                throw new Error('Cart product update failed after applying discount code')
            }
            data.cartRemoveMultiCoupon?.cart && setCartData(data.cartRemoveMultiCoupon.cart)
            $bus.$emit('notification', {
                message: $i18n.t('shcC.cart.notification.discountCodeDelete', {
                    code: separateDiscount?.couponCode ?? '',
                }),
                type: 'success',
            })
        }),
    )

    $bus.$on('cartNextStep', async ({ callback }) => {
        await actionLoading(async () => await getCartData())

        if (store.errors?.length !== 0) {
            return
        }
        callback()
    })
})
